/**
 * Lesson *engine* — the controller that ties content + progress together.
 *
 * It owns the navigation cursor ("which chapter / which step am I on"),
 * persists completion to storage as the kid advances, and exposes the
 * immediate-window submission seam (`submitExercise`). It is DOM-free and
 * notifies subscribers on every state change so the view (`lesson-view.ts`)
 * can re-render — the same model/controller split used by the layout.
 *
 * State model:
 *   - `currentChapter === null`  → the kid is on the lesson **menu**.
 *   - `currentChapter !== null`  → a chapter is **open** at `currentStepIndex`.
 */
import {
  evaluateSuccessCheck,
  type Chapter,
  type ExerciseContext,
  type Step,
} from "./content";
import {
  chapterCompletion,
  isStepComplete as isStepCompleteIn,
  loadProgress,
  markStepComplete,
  saveProgress,
  type ChapterStatus,
  type ProgressState,
  type ProgressStorage,
} from "./progress";

/** Result of handing an exercise attempt to the engine. */
export interface SubmitResult {
  /** Whether the step's success check was satisfied. */
  passed: boolean;
  /** Whether the engine advanced to a next step as a result. */
  advanced: boolean;
}

/** Public surface the view (and tests) drive. */
export interface LessonEngine {
  /** All chapters, in menu order. */
  readonly chapters: Chapter[];
  /** The open chapter, or null when on the menu. */
  readonly currentChapter: Chapter | null;
  /** The current step within the open chapter, or null when on the menu. */
  readonly currentStep: Step | null;
  /** Index of the current step within the open chapter (-1 on the menu). */
  readonly currentStepIndex: number;

  /** Open a chapter at its first step. No-op for an unknown id. */
  openChapter(chapterId: string): void;
  /** Return to the lesson menu. */
  closeChapter(): void;
  /** Advance to the next step. Returns false if already on the last step. */
  nextStep(): boolean;
  /** Go back a step. Returns false if already on the first step. */
  prevStep(): boolean;
  /** Jump to a step by index within the open chapter (clamped/ignored if invalid). */
  goToStep(index: number): void;

  /** Mark the current step complete (persisted) and advance if possible. */
  completeCurrentStep(): void;
  /**
   * The immediate-window seam: evaluate the current step's success check
   * against what the kid did. On pass, the step is completed and the engine
   * advances. #701 calls this after running the kid's code.
   */
  submitExercise(ctx: ExerciseContext): SubmitResult;

  /** Whether a specific step is complete. */
  isStepComplete(chapterId: string, stepId: string): boolean;
  /** Completion status for a chapter (drives the menu `*` marker). */
  chapterStatus(chapterId: string): ChapterStatus;

  /** Subscribe to state changes; returns an unsubscribe function. */
  onChange(listener: () => void): () => void;
}

export interface LessonEngineOptions {
  /** The curriculum to drive. */
  chapters: Chapter[];
  /**
   * Where to persist progress. Defaults to the browser `localStorage` when
   * available, otherwise an ephemeral in-memory store (e.g. private mode / SSR
   * / tests that don't inject one).
   */
  storage?: ProgressStorage;
}

/** A process-local fallback store when no real `localStorage` is available. */
function memoryStorage(): ProgressStorage {
  const data = new Map<string, string>();
  return {
    getItem: (k) => (data.has(k) ? (data.get(k) as string) : null),
    setItem: (k, v) => {
      data.set(k, v);
    },
  };
}

/** Resolve a default storage backend safely (no `localStorage` reference throws). */
function defaultStorage(): ProgressStorage {
  try {
    const ls = (globalThis as { localStorage?: ProgressStorage }).localStorage;
    if (ls) return ls;
  } catch {
    /* access can throw in some sandboxes — fall through to memory. */
  }
  return memoryStorage();
}

/** Create a lesson engine over the given chapters + storage. */
export function createLessonEngine(options: LessonEngineOptions): LessonEngine {
  const chapters = options.chapters;
  const storage = options.storage ?? defaultStorage();

  let progress: ProgressState = loadProgress(storage);
  let chapterId: string | null = null;
  let stepIndex = -1;
  const listeners = new Set<() => void>();

  const findChapter = (id: string | null): Chapter | null =>
    id === null ? null : (chapters.find((c) => c.id === id) ?? null);

  const emit = (): void => {
    for (const listener of listeners) listener();
  };

  const persist = (): void => saveProgress(storage, progress);

  const api: LessonEngine = {
    get chapters() {
      return chapters;
    },
    get currentChapter() {
      return findChapter(chapterId);
    },
    get currentStep() {
      const chapter = findChapter(chapterId);
      if (!chapter) return null;
      return chapter.steps[stepIndex] ?? null;
    },
    get currentStepIndex() {
      return chapterId === null ? -1 : stepIndex;
    },

    openChapter(id) {
      const chapter = findChapter(id);
      if (!chapter) return; // unknown id — ignore.
      chapterId = chapter.id;
      stepIndex = 0;
      emit();
    },

    closeChapter() {
      if (chapterId === null) return;
      chapterId = null;
      stepIndex = -1;
      emit();
    },

    nextStep() {
      const chapter = findChapter(chapterId);
      if (!chapter || stepIndex >= chapter.steps.length - 1) return false;
      stepIndex += 1;
      emit();
      return true;
    },

    prevStep() {
      if (chapterId === null || stepIndex <= 0) return false;
      stepIndex -= 1;
      emit();
      return true;
    },

    goToStep(index) {
      const chapter = findChapter(chapterId);
      if (!chapter) return;
      if (index < 0 || index >= chapter.steps.length || index === stepIndex) {
        return;
      }
      stepIndex = index;
      emit();
    },

    completeCurrentStep() {
      const chapter = findChapter(chapterId);
      const step = chapter?.steps[stepIndex];
      if (!chapter || !step) return;
      progress = markStepComplete(progress, chapter.id, step.id);
      persist();
      // Advance if there's somewhere to go; either way notify (marker changed).
      if (stepIndex < chapter.steps.length - 1) {
        stepIndex += 1;
      }
      emit();
    },

    submitExercise(ctx) {
      const chapter = findChapter(chapterId);
      const step = chapter?.steps[stepIndex];
      if (!chapter || !step) return { passed: false, advanced: false };

      const passed = evaluateSuccessCheck(step.successCheck, ctx);
      if (!passed) return { passed: false, advanced: false };

      const before = stepIndex;
      this.completeCurrentStep();
      return { passed: true, advanced: stepIndex !== before };
    },

    isStepComplete(cid, stepId) {
      return isStepCompleteIn(progress, cid, stepId);
    },

    chapterStatus(cid) {
      const chapter = findChapter(cid);
      if (!chapter) return { done: 0, total: 0, complete: false };
      return chapterCompletion(chapter, progress);
    },

    onChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };

  return api;
}
